import { useEffect, useState } from 'react'
import { ArrowLeftRight, ArrowRight } from 'lucide-react'
import { api } from '@/util/api'
import { AdminLayout } from '@/components/admin/AdminLayout'
import {
  Badge,
  Button,
  IconButton,
  IconButtonLink,
  Notice,
  Skeleton,
  SkeletonGroup,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@/components/play'
import { useAuth } from '@/state/AuthContext'
import type { OrgResource } from '@/types/admin'

const PLAN_LABELS: Record<string, string> = { free: 'Free', starter: 'Starter', pro: 'Pro', enterprise: 'Enterprise' }

export function AdminOrganizationsPage() {
  const [organizations, setOrganizations] = useState<OrgResource[]>([])
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const { switchOrganization } = useAuth()

  function loadOrganizations() {
    setLoading(true)
    setFailed(false)
    api.getAdminOrganizations()
      .then((res) => setOrganizations(res.data))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false))
  }

  useEffect(loadOrganizations, [])

  return (
    <AdminLayout>
      <div className="space-y-6">
        <h1 className="text-3xl font-semibold">Organizations</h1>

        {loading ? (
          <SkeletonGroup label="Loading organizations" className="grid gap-2">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}
          </SkeletonGroup>
        ) : failed ? (
          <Notice tone="error" action={<Button variant="secondary" size="compact" onClick={loadOrganizations}>Try again</Button>}>
            We couldn’t load organizations.
          </Notice>
        ) : (
          <Table caption="Organizations" hideCaption>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Slug</TableHeaderCell>
                <TableHeaderCell>Plan</TableHeaderCell>
                <TableHeaderCell>AI Producer</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Created</TableHeaderCell>
                <TableHeaderCell type="actions"><span className="sr-only">Actions</span></TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {organizations.map((org) => (
                <TableRow key={org.id}>
                  <TableHeaderCell scope="row">{org.attributes.name}</TableHeaderCell>
                  <TableCell type="data" className="text-neutral-7">{org.attributes.slug}</TableCell>
                  <TableCell>
                    <Badge tone="neutral">{PLAN_LABELS[org.attributes.subscription_plan] ?? org.attributes.subscription_plan}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge tone={org.attributes.director_enabled ? 'neutral' : 'quiet'}>
                      {org.attributes.director_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge tone={org.attributes.active ? 'neutral' : 'quiet'}>
                      {org.attributes.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell type="data" className="text-neutral-7">
                    {new Date(org.attributes.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell type="actions">
                    <div className="flex justify-end gap-1">
                      <IconButton
                        label={`Switch to ${org.attributes.name}`}
                        variant="ghost"
                        size="compact"
                        onClick={() => switchOrganization(org.id)}
                      >
                        <ArrowLeftRight />
                      </IconButton>
                      <IconButtonLink to={`/admin/organizations/${org.id}`} variant="ghost" size="compact" label={`Open ${org.attributes.name}`}>
                        <ArrowRight />
                      </IconButtonLink>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {organizations.length === 0 && (
                <TableEmpty colSpan={7}>No organizations yet</TableEmpty>
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </AdminLayout>
  )
}
